<template>
  <v-card min-height="80vh">
    <v-card-title class="primary white--text">
      <span class="headline">
        {{title}}
      </span>
      <v-spacer/>
      <BaseDialogActions hideRefresh/>
    </v-card-title>
    <v-card-text>
      <v-container fluid>
        <v-row v-if="!loader.loading" dense>
          <v-col>
            <v-chip>
              <span>{{dateRangeAsString}}</span>
            </v-chip>
          </v-col>
          <v-col cols="12">
            <v-row dense>
              <v-col cols="6" sm="12" md="6" v-for="table in summaryTables" :key="table.label">
                <DetailCard :title="table.label">
                  <v-row v-for="item in table.items" :key="`ts-table-${item.label}`" dense>
                    <v-col>
                      <v-tooltip bottom v-if="item.tooltip" color="primary">
                        <template #activator="{on}">
                          <span v-on="on">{{(item.label)}}:</span>
                        </template>
                        <span class="subtitle-2 font-weight-regular white--text">{{item.tooltip}}</span>
                      </v-tooltip>
                      <span v-else>{{$t(item.label)}}:</span>
                    </v-col>
                    <v-col cols="auto">
                      <span :style="`text-align: ${item.value?.[0] === '$' ? 'right' : 'unset'}`">{{item.value}}</span>
                    </v-col>
                  </v-row>
                </DetailCard>
              </v-col>
            </v-row>
          </v-col>
          <v-col cols="12" v-if="!loader.error" class="mt-2">
            <v-row dense justify="end">
              <v-col cols="auto">
                <ColumnAdjuster
                :propColumnSet.sync="speciesColumnSet"
                @new-columns-selected="newSpeciesColumnsSelected"/>
              </v-col>
              <v-col cols="12">
                <v-expansion-panels popout v-model="openPanels" multiple>
                  <v-expansion-panel v-for="destination in destinations" :key="destination.accountId">
                    <v-expansion-panel-header active-class="primary white--text" hide-actions>
                      <span>{{ destination.accountName }}</span>
                    </v-expansion-panel-header>
                    <v-expansion-panel-content>
                      <DataTable
                      :headers.sync="detailedSummaryHeaders"
                      :items="destination.speciesSummaries"
                      :sumHeaders="detailedSummaryHeaders.filter(h => h.total !== undefined).map(c => c.value)"
                      showTotals
                      hideHeader/>
                    </v-expansion-panel-content>
                  </v-expansion-panel>
                </v-expansion-panels>
              </v-col>
            </v-row>
          </v-col>
          <div v-else style="text-align: center; width: 100%;" class="pa-6">
            <span class="title font-italic">{{$t('errorWhileLoadingX', { x: $t('tractcostDetail') })}}</span>
          </div>
        </v-row>
        <Loading v-else/>
      </v-container>
    </v-card-text>
  </v-card>
</template>

<script>
import { mapActions } from 'vuex'
import { companyOffsetToLocalDate } from '@/utils/DateFormatter.js'
import TractHeaders from '@/headers/Tract.js'
import { ResourceLoader } from '@/utils/ResourceLoader'
import { TractTypeCategory, EnumValueWithIcon } from '@/utils/Enumerations'
export default {
  name: 'TractSpeciesCostDetail',

  props: {
    tractSummary: {
      type: Object,
      required: true
    },
    dateRange: {
      type: Object,
      required: true
    },
    entity: {
      type: Object,
      required: true
    },
    tractTypeCategory: {
      type: EnumValueWithIcon,
      required: true
    }
  },

  components: {
    BaseDialogActions: () => import('@/components/core/BaseDialogActions.vue'),
    ColumnAdjuster: () => import('@/components/core/ColumnAdjuster.vue'),
    Loading: () => import('@/components/core/Loading.vue'),
    DataTable: () => import('@/components/core/table/DataTable.vue'),
    DetailCard: () => import('@/components/core/DetailCard.vue')
  },

  data: () => ({
    openPanels: [],
    ticketColumnSet: null,
    loading: false,
    loader: ResourceLoader.empty,
    tractCostSummary: undefined,
    speciesColumnSet: null
  }),

  computed: {
    detailedSummaryHeaders () {
      if (!this.speciesColumnSet) return []
      return this.speciesColumnSet.getColumns()
    },

    summary () {
      return this.tractCostSummary?.summary
    },

    destinations () {
      if (this.tractCostSummary === undefined) return []
      return this.tractCostSummary.destinationsInPeriod
    },

    title () {
      return `${this.entity.name} || Species for ${this.tractSummary.name} by Destination`
    },

    dateRangeAsString () {
      return `${companyOffsetToLocalDate(this.dateRange.sinceTime)} - ${companyOffsetToLocalDate(this.dateRange.untilTime, 'L')}`
    },

    isStumpage () {
      return this.tractTypeCategory === TractTypeCategory.Stumpage
    },

    summaryTables () {
      return [
        {
          label: this.$t('tractSummary'),
          hidden: false,
          items: [
            {
              label: ('tractType'),
              value: this.summary.tractType
            }
          ]
        }
      ]
    }
  },

  async created () {
    this.speciesColumnSet = TractHeaders.detailedSummaryBySpeciesHeaders(this.tractTypeCategory.value)
    this.loader = new ResourceLoader(async () => {
      await this.refresh(this.tractSummary.tractId, this.dateRange)
    })
    await this.loader.load()
    this.openPanels = this.destinations.length === 1 ? [0] : []
  },

  methods: {
    ...mapActions('tract', ['getDetailedTractSpeciesCosts']),

    newSpeciesColumnsSelected (columnSet) {
      this.speciesColumnSet = columnSet
    },

    async refresh (tractId, timeframe) {
      const response = await this.getDetailedTractSpeciesCosts({
        tractId: tractId,
        businessEntityId: this.entity.businessEntityId,
        params: {
          ...timeframe
        }
      })
      this.tractCostSummary = response
    }
  }
}
</script>
